/**
 * CSV post-processing utilities for Power Log export
 * Formats currency columns in backend-generated CSV before download
 */

import { formatCurrencyWithSymbol } from './formatters';

const CURRENCY_COLUMNS = ['Bond Amount', 'Premium Charged', 'Premium To Company'];

/**
 * Parse a single CSV row respecting quoted fields (RFC 4180).
 * Handles fields like "Smith, John" that contain commas.
 * @param {string} line - A single CSV line
 * @returns {string[]} Array of field values
 */
export const parseCSVRow = (line) => {
  const fields = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"') {
        if (i + 1 < line.length && line[i + 1] === '"') {
          current += '"';
          i++; // skip escaped quote
        } else {
          inQuotes = false;
        }
      } else {
        current += ch;
      }
    } else {
      if (ch === '"') {
        inQuotes = true;
      } else if (ch === ',') {
        fields.push(current);
        current = '';
      } else {
        current += ch;
      }
    }
  }
  fields.push(current);
  return fields;
};

/**
 * Wrap a value in quotes if it contains commas, quotes, or newlines.
 * @param {string} value - Cell value
 * @returns {string} Properly escaped CSV cell
 */
export const csvCell = (value) => {
  if (value == null) return '';
  const str = String(value);
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
};

/**
 * Post-process CSV text to format currency columns with $ symbol and commas.
 * Finds Bond Amount, Premium Charged, and Premium columns by header name
 * and formats their values using formatCurrencyWithSymbol.
 * @param {string} csvText - Raw CSV text from backend
 * @returns {string} CSV text with formatted currency columns
 */
export const formatCSVCurrencyColumns = (csvText) => {
  // Normalize line endings
  const normalized = csvText.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  const lines = normalized.split('\n');

  if (lines.length === 0) return csvText;

  // Parse header row to find currency column indices
  const headers = parseCSVRow(lines[0]);
  const currencyIndices = [];
  headers.forEach((header, index) => {
    if (CURRENCY_COLUMNS.includes(header.trim())) {
      currencyIndices.push(index);
    }
  });

  // No currency columns found — return as-is
  if (currencyIndices.length === 0) return csvText;

  // Re-serialize header row (unchanged)
  const outputLines = [lines[0]];

  // Process data rows
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i];
    if (!line.trim()) {
      outputLines.push(line);
      continue;
    }

    const fields = parseCSVRow(line);

    for (const idx of currencyIndices) {
      if (idx < fields.length) {
        const raw = fields[idx].trim();
        if (raw === '' || raw === null) continue;
        const num = parseFloat(raw);
        if (!isNaN(num)) {
          fields[idx] = formatCurrencyWithSymbol(num);
        }
      }
    }

    outputLines.push(fields.map(csvCell).join(','));
  }

  return outputLines.join('\n');
};
